/**
 * How a page arrives when you change pages (Stage 3, Sam 2026-10-21: "more of an animated feel than a snap"). Enter-only:
 * the new page animates in over the old one's last frame, so nothing waits on the page that is leaving.
 *
 * Between dock tabs the page comes in from the side of the tab you tapped (Events is leftmost); going deeper inside a
 * section (a group, an event) it comes in from the right, and back out from the left. ?page=<variant> in a dev build
 * picks another (lib/motionFlags, /lab/motion).
 */

/** The dock's order (Header dockTabs). A path outside the dock has no tab. */
const TAB_ORDER = [
  [/^\/(events(\/|$)|$)/, 0],
  [/^\/chats(\/|$)/, 1],
  [/^\/sets(\/|$)/, 2],
  [/^\/map(\/|$)/, 3],
  [/^\/find(\/|$)/, 4],
  [/^\/groups(\/|$)/, 5],
];
export function tabOf(pathname) {
  for (const [re, i] of TAB_ORDER) if (re.test(pathname)) return i;
  return null;
}
const depthOf = (pathname) => pathname.split('/').filter(Boolean).length;

/** +1: the page comes in from the right; -1: from the left; 0: no side (a snap). */
export function directionOf(from, to) {
  const a = tabOf(from),
    b = tabOf(to);
  if (a != null && b != null && a !== b) return b > a ? 1 : -1;
  const da = depthOf(from),
    db = depthOf(to);
  if (db !== da) return db > da ? 1 : -1;
  return 0;
}

// Every variant moves the page without fading it (Sam's pick, 2026-10-31): while a page fades in WebKit blurs nothing
// behind it (an ancestor below opacity 1 is a backdrop root), so the glass cards flashed sharp sparkles and then snapped
// to frosted. Moving it at full opacity keeps the blur real from the first frame.
export const PAGE_TRANSITIONS = {
  off: { label: 'Snap', note: 'The page is simply there.' },
  slide: {
    label: 'Slide',
    note: 'The new page slides in a short way from the side of the tab you tapped (deeper pages from the right).',
  },
  push: {
    label: 'Push',
    note: 'Like Slide, further and a touch slower: closer to an iPhone push.',
  },
  rise: { label: 'Rise', note: 'The new page rises a little and settles. No side.' },
};

const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

/** Animate `el` in as the page that just arrived. With no side to come from, or with reduced motion, it snaps. */
export function playPageEnter(el, variant, dir) {
  if (!el?.animate || !variant || variant === 'off') return;
  const reduce =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return;
  let frames, duration;
  if (variant === 'rise') {
    frames = [{ transform: 'translateY(14px) scale(0.985)' }, { transform: 'none' }];
    duration = 300;
  } else if ((variant === 'slide' || variant === 'push') && dir) {
    frames = [
      { transform: `translateX(${(variant === 'push' ? 64 : 28) * dir}px)` },
      { transform: 'none' },
    ];
    duration = variant === 'push' ? 340 : 260;
  } else return;
  el.getAnimations?.().forEach((a) => a.cancel()); // a quick second tap starts over from the new page
  el.animate(frames, { duration, easing: EASE });
}
